import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import apiClient from '../../utils/api';
import TogglePill from '../ui/TogglePill';

// "Did you take your supplements today?" — one row, three answers. Yes logs
// an intake entry per active item in your stack; Some reveals the stack so
// you can untick what you missed; No logs nothing and never counts against
// you. Renders as a row inside Home's question list.

const todayStr = () => new Date().toISOString().split('T')[0];

const OPTIONS = [
  { status: 'yes', label: 'Yes' },
  { status: 'mostly', label: 'Some' },
  { status: 'no', label: 'No' },
];

const StackConfirmCard = ({ onEntrySaved, framed = false }) => {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [excluded, setExcluded] = useState([]);
  const [showStack, setShowStack] = useState(false);
  const day = todayStr();

  const load = useCallback(async () => {
    try {
      const res = await apiClient.get('/api/stack-confirm', { params: { target_date: day } });
      setData(res.data);
      setExcluded(res.data?.excluded_items || []);
      setShowStack(res.data?.status === 'mostly');
    } catch {
      // Silent — the row just doesn't render if the fetch fails.
    }
  }, [day]);

  useEffect(() => { load(); }, [load]);

  const confirm = async (status, excludedItems) => {
    setSaving(true);
    try {
      const res = await apiClient.put('/api/stack-confirm', { status, date: day, excluded_items: excludedItems || [] });
      setData(res.data);
      setExcluded(res.data?.excluded_items || []);
      if (res.data?.generated_entries && onEntrySaved) onEntrySaved();
    } catch {
      toast.error('Could not save that just now');
    } finally {
      setSaving(false);
    }
  };

  const onPick = (status) => {
    if (status === 'mostly') { setShowStack(true); confirm('mostly', excluded); }
    else { setShowStack(false); confirm(status, []); }
  };

  const toggleExcluded = (id) => {
    const next = excluded.includes(id) ? excluded.filter((x) => x !== id) : [...excluded, id];
    setExcluded(next);
    confirm('mostly', next);
  };

  if (!data) return null;
  const supplements = data.supplements || [];
  if (supplements.length === 0) return null;
  const status = data.status;

  return (
    <div className={`grid gap-3 px-5 py-3.5 sm:grid-cols-[200px_1fr] sm:items-center sm:gap-4 ${framed ? 'rounded-xl border border-line' : ''}`}>
      <div className="text-[1.05rem] font-medium">Did you take your supplements today?</div>
      <div>
        <div className="flex flex-wrap gap-2">
          {OPTIONS.map((o) => (
            <TogglePill key={o.status} on={status === o.status} disabled={saving} onClick={() => onPick(o.status)}>
              {o.label}
            </TogglePill>
          ))}
        </div>
        {(showStack || status === 'mostly') && (
          <div className="mt-3 flex flex-wrap gap-2">
            {supplements.map((s) => {
              const taken = !excluded.includes(s.id);
              return (
                <button
                  key={s.id} type="button" disabled={saving} onClick={() => toggleExcluded(s.id)}
                  className={`rounded-full border px-3 py-1 text-sm transition-colors disabled:opacity-70 ${
                    taken ? 'border-fg-3 text-fg' : 'border-line text-fg-3 line-through'
                  }`}
                >
                  {s.name}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default StackConfirmCard;
